<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router';
import type { MenuGroup } from '../types/common';

const props = defineProps<{
    menuGroups: MenuGroup[];
    showSearch?: boolean;
    collapsible?: boolean;
}>();

const { t } = useI18n();
const route = useRoute();
const searchQuery = ref('');

// expanded state for each menuGroup
const expandedGroups = ref<Record<number, boolean>>({});

const navigate = (href: RouteLocationRaw | undefined) => {
    if (href) {
        navigateTo(href);
    }
};

const toggleGroup = (index: number) => {
    if (props.collapsible) {
        expandedGroups.value[index] = !expandedGroups.value[index];
    }
};

const handleGroupClick = (index: number, href: RouteLocationRaw | undefined) => {
    if (href) {
        navigate(href);
    }
    if (props.collapsible) {
        toggleGroup(index);
    }
};

const isActive = (path?: string | RouteLocationRaw) => {
    if (!path) { return false; }
    
    const currentPath = route.path;
    return route.path === path || currentPath.startsWith(`${path}/`);
};

const isExactMatch = (path?: string | RouteLocationRaw) => {
    if (!path) { return false; }
    return route.path === path;
};

const hasActiveItem = (group: MenuGroup) => {
    return group.items?.some(item => isActive(item.href)) || false;
};

watch(() => route.path, () => {
    props.menuGroups.forEach((group, index) => {
        if (hasActiveItem(group)) {
            expandedGroups.value[index] = true;
        }
    });
}, { immediate: true });


const getItemLabel = (item: { labelKey?: string; label?: string }) => {
    return item.labelKey ? t(item.labelKey) : item.label || '';
};

const filteredMenuGroups = computed(() => {
    if (!searchQuery.value.trim()) { return props.menuGroups; }
    
    const query = searchQuery.value.toLowerCase();
    return props.menuGroups
        .map(group => ({
            ...group,
            items: group.items?.filter((item) => {
                return getItemLabel(item).toLowerCase().includes(query);
            }) || []
        }))
        .filter(group => group.items.length > 0);
});

</script>

<template>
    <main class="flex min-h-full h-full gap-4">
        <aside 
            class="h-full w-2/12 flex flex-col overflow-hidden sidebar-border pr-2"
        >
            <div v-if="showSearch" class="py-2">
                <SearchInput
                    v-model="searchQuery"
                    :placeholder="t('common.search')"
                />
            </div>

            <nav class="flex-1 overflow-y-auto space-y-3">
                <div
                    v-for="(mg, index) in filteredMenuGroups"
                    :key="mg.labelKey || mg.label || index"
                >
                    <div
                        class="menu-group-header flex items-center rounded-lg justify-between p-2"
                        :class="{
                            'highlighted': isExactMatch(mg.href),
                            'cursor-pointer': collapsible || mg.href
                        }"
                        @click="handleGroupClick(index, mg.href)"
                    >
                        <div class="flex items-center">
                            <div v-if="mg.icon">
                                <i :class="mg.icon" class="mr-2 text-sm" />
                            </div>
                            <label class="cursor-pointer">
                                {{ mg.labelKey ? t(mg.labelKey) : mg.label }}
                            </label>
                        </div>
                        <i
                            v-if="collapsible"
                            :class="expandedGroups[index] ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                            class="text-xs transition-transform"
                        />
                    </div>

                    <div 
                        v-show="!collapsible || expandedGroups[index]"
                        class="items-container overflow-hidden"
                    >
                        <NuxtLink
                            v-for="(item, i) in mg.items"
                            :key="i"
                            :to="item.href || '#'"
                            :target="item.externalLink ? '_blank' : '_self'"
                            class="menu-item flex items-center py-2 px-8 rounded-lg cursor-pointer font-normal no-underline"
                            :class="{'highlighted': isActive(item.href)}"
                        >
                            {{ item.labelKey ? t(item.labelKey) : item.label }}
                            <i v-if="item.externalLink" :class="item.icon" class="ml-2 text-xs" />
                        </NuxtLink>
                    </div>
                </div>
            </nav>
        </aside>

        <div 
            class="h-full w-10/12 rounded-md p-2 overflow-auto"
        >
            <slot />
        </div>
    </main>
</template>

<style scoped>
.sidebar-border {
    border-right: 1px solid var(--primary-color-disabled);
}

.menu-item, .menu-group-header {
    color: var(--primary-color);
    transition: all 0.2s ease;
}

.menu-item:hover,
.menu-group-header:hover {
    color: var(--secondary-color);
    background-color: var(--secondary-color-selected-dark);
}

.highlighted {
    color: var(--primary-color);
    background-color: var(--secondary-color-selected-dark);
}
</style>
